// Shared (client-safe) types and validation for the public Palworld TCG
// want-list board. No Supabase import here, so route files can pull the types
// without dragging server code into the browser bundle.
import { z } from "zod";

export type WantKind = "want" | "have";

export interface WantEntry {
  id: string;
  kind: WantKind;
  card_code: string;
  note: string;
  owner_name: string;
  created_at: string;
}

/** One card with everyone looking for it and everyone holding a spare. */
export interface WantBoardCard {
  code: string;
  wants: WantEntry[];
  haves: WantEntry[];
}

export const wantEntrySchema = z.object({
  kind: z.enum(["want", "have"]),
  cardCode: z.string().trim().min(3).max(24),
  note: z.string().trim().max(200).default(""),
});

export const wantIdSchema = z.object({ id: z.string().uuid() });

export const WANT_LIMIT = 40;

/** Board rows are grouped per card so a visitor sees both sides at a glance. */
export function groupWantBoard(rows: WantEntry[]): WantBoardCard[] {
  const map = new Map<string, WantBoardCard>();
  for (const row of rows) {
    const code = row.card_code.toUpperCase();
    const entry = map.get(code) ?? { code, wants: [], haves: [] };
    if (row.kind === "have") entry.haves.push(row);
    else entry.wants.push(row);
    map.set(code, entry);
  }
  return [...map.values()].sort(
    (a, b) =>
      b.wants.length + b.haves.length - (a.wants.length + a.haves.length) ||
      a.code.localeCompare(b.code),
  );
}

/** A card is a match when at least one member wants it and another has it spare. */
export function isMatched(card: WantBoardCard): boolean {
  return card.wants.length > 0 && card.haves.length > 0;
}
